import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Text,
  Spinner
} from '@fluentui/react-components';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle
} from '@fluentui/react-components/unstable';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ServiceFilterCriteria } from '../ServiceList/ServiceList.Menu';
import ProtocolSelectorComponent from '../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent';
import DateRangeSelectorComponent from '../../../commons/component/Inputs/DateRangeSelector/DateRangeSelector.Component';
import { ExportServicesByProtocol } from '../../../services/Service.Services';
import {
  buildExportFileName,
  buildServiceExportFilter
} from './ServiceExportDrawer.Functions';
import { useServiceExportDrawerStyles } from './ServiceExportDrawer.Styles';
import { AppState } from '../../../redux/reducers';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import * as MedicalProtocolStore from '../../../redux/middleware/MedicalProtocolMiddleware';

interface ServiceExportDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filterCriteria: ServiceFilterCriteria;
  searchCriteria?: string;
}

const ServiceExportDrawer: React.FC<ServiceExportDrawerProps> = ({
  isOpen,
  onClose,
  filterCriteria,
  searchCriteria
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const styles = useServiceExportDrawerStyles();
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedProtocols, setSelectedProtocols] = useState<Protocol[] | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | undefined>(filterCriteria.startDate);
  const [endDate, setEndDate] = useState<Date | undefined>(filterCriteria.endDate);

  const MedicalProtocolsState = useSelector((state: AppState) => state.MedicalProtocols);

  useEffect(() => {
    if (isOpen) {
      setStartDate(filterCriteria.startDate);
      setEndDate(filterCriteria.endDate);

      if (filterCriteria.protocolId && MedicalProtocolsState?.list?.length) {
        const selectedProtocol = MedicalProtocolsState.list.find((protocol: Protocol) => protocol.id === filterCriteria.protocolId);
        setSelectedProtocols(selectedProtocol ? [selectedProtocol] : undefined);
      } else {
        setSelectedProtocols(undefined);
      }
    }
  }, [isOpen, filterCriteria.endDate, filterCriteria.protocolId, filterCriteria.startDate, MedicalProtocolsState?.list]);

  useEffect(() => {
    if (isOpen && (!MedicalProtocolsState?.list || MedicalProtocolsState.list.length === 0)) {
      dispatch(MedicalProtocolStore.actionCreators.GetAllMedicalProtocols());
    }
  }, [MedicalProtocolsState?.list, dispatch, isOpen]);

  const selectedProtocolId = selectedProtocols && selectedProtocols.length > 0
    ? selectedProtocols[0].id
    : undefined;

  const summaryLines = useMemo(() => {
    const lines: string[] = [];

    const selectedProtocolLabel = selectedProtocols && selectedProtocols.length > 0
      ? selectedProtocols[0].title
      : undefined;
    if (selectedProtocolLabel) {
      lines.push(`${t('servicesListPage.filters.protocol')}: ${selectedProtocolLabel}`);
    }

    if (startDate || endDate) {
      lines.push(
        t('servicesListPage.export.summaryPeriod', {
          defaultValue: 'Periodo: {{start}} - {{end}}',
          start: startDate ? startDate.toLocaleDateString() : '-',
          end: endDate ? endDate.toLocaleDateString() : '-'
        })
      );
    }

    if (searchCriteria) {
      lines.push(
        t('servicesListPage.export.summarySearch', {
          defaultValue: 'Busqueda: {{search}}',
          search: searchCriteria
        })
      );
    }

    if (!lines.length) {
      lines.push(
        t('servicesListPage.export.summaryAll', {
          defaultValue: 'Se exportaran todos los servicios del protocolo seleccionado.'
        })
      );
    }

    return lines;
  }, [startDate, endDate, searchCriteria, selectedProtocols, t]);

  const handleExport = async () => {
    if (selectedProtocolId === undefined || selectedProtocolId === null) {
      setErrorMessage(
        t('servicesListPage.export.protocolRequired', {
          defaultValue: 'Debe seleccionar un protocolo para exportar servicios.'
        })
      );
      return;
    }

    try {
      setErrorMessage(null);
      setIsExporting(true);

      const localFilterCriteria: ServiceFilterCriteria = {
        protocolId: selectedProtocolId,
        startDate,
        endDate
      };

      const filter = buildServiceExportFilter(localFilterCriteria, searchCriteria);
      const blob = await ExportServicesByProtocol(selectedProtocolId, filter);

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = buildExportFileName();
      document.body.appendChild(anchor);
      anchor.click();
      URL.revokeObjectURL(url);
      anchor.remove();

      onClose();
    } catch {
      setErrorMessage(
        t('servicesListPage.export.error', {
          defaultValue: 'No se pudo exportar el archivo. Intente nuevamente.'
        })
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(_, { open }) => !open && onClose()}
      position="end"
      size="small"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label={t('servicesListPage.export.close', { defaultValue: 'Cerrar' })}
              icon={<Dismiss24Regular />}
              onClick={onClose}
            />
          }
        >
          {t('servicesListPage.export.title', { defaultValue: 'Exportar servicios' })}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        <div className={styles.content}>
          <Text className={styles.helperText}>
            {t('servicesListPage.export.description', {
              defaultValue:
                'Descargue un Excel por periodo y protocolo con los filtros actuales.'
            })}
          </Text>

          <div className={styles.formGrid}>
            <ProtocolSelectorComponent
              selectionLimit={1}
              selectedProtocols={selectedProtocols}
              onChangeSelection={setSelectedProtocols}
            />

            <DateRangeSelectorComponent
              startDate={startDate}
              endDate={endDate}
              onDateChange={(nextStartDate, nextEndDate) => {
                setStartDate(nextStartDate);
                setEndDate(nextEndDate);
              }}
              startDateLabel={t('servicesListPage.filters.startDate')}
              endDateLabel={t('servicesListPage.filters.endDate')}
            />
          </div>

          <div className={styles.summaryBox}>
            {summaryLines.map((line) => (
              <Text key={line} className={styles.summaryLine}>
                {line}
              </Text>
            ))}
          </div>

          {errorMessage && (
            <MessageBar intent="error" layout="multiline">
              <MessageBarBody>
                <MessageBarTitle>
                  {t('servicesListPage.export.errorTitle', {
                    defaultValue: 'Error de exportacion'
                  })}
                </MessageBarTitle>
                {errorMessage}
              </MessageBarBody>
            </MessageBar>
          )}
        </div>
      </DrawerBody>

      <DrawerFooter>
        <Button onClick={onClose}>
          {t('servicesListPage.export.cancel', { defaultValue: 'Cancelar' })}
        </Button>
        <Button appearance="primary" onClick={handleExport} disabled={isExporting}>
          {isExporting ? (
            <Spinner size="tiny" />
          ) : (
            t('servicesListPage.export.download', { defaultValue: 'Descargar Excel' })
          )}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

export default ServiceExportDrawer;
